import { Component, HostListener, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Platform } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import { logoIonic } from 'ionicons/icons';
import { register } from 'swiper/element/bundle';

import {
  MAINTENANCE_PAGE,
  NavigationService
} from '@core/services/navigation/navigation.service';
import { TranslationService } from '@core/services/translation/translation.service';
import { WindowMeasuresService } from '@core/services/window-measures/window-measures.service';

import { environment } from '@environments/environment';

import { APP_IMPORTS } from './app.imports';

declare let gtag: (command: string, key: string, ...args: unknown[]) => void;

register();

@Component({
  standalone: true,
  selector: 'el-buen-inquilino-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [...APP_IMPORTS]
})
export class AppComponent implements OnInit {
  private platform = inject(Platform);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private translationService = inject(TranslationService);
  private windowMeasuresService = inject(WindowMeasuresService);
  private navigationService = inject(NavigationService);

  constructor() {
    addIcons({ logoIonic });
    this.initializeApp();
  }

  @HostListener('window:resize', ['$event'])
  windowChangeListener(_ev?: Event): void {
    this.windowMeasuresService.setInnerWidth(window.innerWidth);
  }

  initializeApp(): void {
    this.platform.ready().then(() => {
      this.translateService.addLangs(['en', 'es']);
      this.translateService.setFallbackLang('es');

      this.translationService.setAppLanguage(
        localStorage.getItem('language') || 'es'
      );
    });
  }

  ngOnInit(): void {
    this.checkOnMaintenance();
    this.windowChangeListener();
    this.configGoogleAnalytics();
    this.configZendesk();
    this.configOsano();
    this.configGoogleMaps();
  }

  private checkOnMaintenance(): void {
    if (environment.maintenance && this.router.url !== MAINTENANCE_PAGE) {
      this.navigationService.goMaintenancePage();
    }
  }

  private configGoogleAnalytics(): void {
    if (environment.googleAnalytics.active) {
      const sub = this.router.events.subscribe((event: unknown) => {
        if (event instanceof NavigationEnd) {
          gtag('config', environment.googleAnalytics.key);
          sub.unsubscribe();
        }
      });
    }
  }

  private configZendesk(): void {
    if (environment.zendesk.active) {
      const sub = this.router.events.subscribe(() => {
        const node = document.createElement('script');
        node.id = environment.zendesk.id;
        node.src = environment.zendesk.url + environment.zendesk.key;
        node.type = 'text/javascript';
        document.getElementsByTagName('body')[0].appendChild(node);
        sub.unsubscribe();
      });
    }
  }

  private configOsano(): void {
    if (environment.osano.active) {
      const sub = this.router.events.subscribe((event: unknown) => {
        if (event instanceof NavigationEnd) {
          if (!event.url.includes('certifications')) {
            const node = document.createElement('script');
            node.src =
              environment.osano.url +
              environment.osano.key +
              environment.osano.file;
            node.type = 'text/javascript';
            node.async = true;
            document.getElementsByTagName('head')[0].appendChild(node);
            sub.unsubscribe();
          }
        }
      });
    }
  }

  private configGoogleMaps(): void {
    const node = document.createElement('script');
    const src = environment.google_maps.url + environment.google_maps.apiKey;
    node.src = src;
    node.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(node);
  }
}
